/**
 * SENTRY INTEGRATION - Error Boundary Component
 *
 * Purpose: Catches JavaScript errors in React component tree and reports them to Sentry.
 *
 * How it works in Sentry process:
 * - Wraps the entire app in index.js
 * - When any React component throws an error, this boundary catches it
 * - Error is automatically sent to Sentry with component stack trace
 * - User sees a friendly fallback UI instead of a white screen
 * - No dialogs or prompts are shown to users (showDialog={false})
 */
import React from "react";
import * as Sentry from "@sentry/react";
import {Button, Result} from "antd";

/**
 * This component is used to display a fallback UI when an error occurs.
 */
const ErrorFallback = ({error}) => {
  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = "/";
  };

  return (
    <div className="error-boundary-fallback">
      <Result
        status="error"
        title="Something went wrong"
        subTitle="We're sorry, but something unexpected happened. Our team has been notified."
        extra={[
          <Button type="primary" key="reload" onClick={handleReload}>
            Reload Page
          </Button>,
          <Button key="home" onClick={handleGoHome}>
            Go to Home
          </Button>,
        ]}
      />
      {process.env.NODE_ENV === "development" && error && (
        <div className="error-boundary-details">
          <details style={{padding: "16px", background: "#fff2f0", margin: "16px"}}>
            <summary style={{cursor: "pointer", fontWeight: "bold"}}>
              Error Details (Development Only)
            </summary>
            <pre
              style={{
                marginTop: "8px",
                padding: "12px",
                background: "#f5f5f5",
                overflow: "auto",
                fontSize: "12px",
              }}
            >
              {error.toString()}
              {error.stack && `\n\nStack trace:\n${error.stack}`}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
};

export const SentryErrorBoundary = ({children, fallback}) => {
  const handleError = (error, componentStack, eventId) => {
    // Log additional context in development only
    if (process.env.NODE_ENV === "development") {
      console.group("[SentryErrorBoundary] Caught error");
      console.error("Error:", error);
      console.error("Component Stack:", componentStack);
      console.info("Sentry Event ID:", eventId);
      console.groupEnd();
    }
  };

  return (
    <Sentry.ErrorBoundary
      fallback={({error, resetError}) =>
        fallback ? fallback({error, resetError}) : <ErrorFallback error={error} />
      }
      onError={handleError}
      showDialog={false}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default SentryErrorBoundary;